import styled from '@emotion/styled'
import { Button, Form, Input, message } from 'antd'
import BigNumber from 'bignumber.js'
import clsx from 'clsx'
import type { Dispatch, SetStateAction } from 'react'
import { useMemo } from 'react'
import { formatEther } from 'viem'
import { useAccount, useBalance } from 'wagmi'

import CopyCode from '@/assets/copy-code-light.png'
import { useLocalStorage } from '@/context/localstorage-context'
import type { BridgeGroupConfigType } from '@/types/storeman-type'
import type { OpenStoremanStakeInTransParamsInterface } from '@/types/trans-params'
import {
  checkAmountUnit,
  formatBalance,
  handleCopyCode,
  hexCharCodeToStr,
} from '@/utils'
export default function StepTwo_1({
  canRegist,
  tarnsParam,
  OpenStoremanGroupRegister,
  setStep,
  setTransParam,
}: {
  canRegist: boolean
  tarnsParam: OpenStoremanStakeInTransParamsInterface
  OpenStoremanGroupRegister: BridgeGroupConfigType
  setStep: Dispatch<SetStateAction<number>>
  setTransParam: Dispatch<
    SetStateAction<OpenStoremanStakeInTransParamsInterface>
  >
}) {
  const [form] = Form.useForm()
  const { address } = useAccount()
  const { theme } = useLocalStorage()
  const isDark = useMemo(() => theme === 'dark', [theme])
  const { data, status } = useBalance({ address })
  const balance = useMemo(() => {
    return data && status === 'success'
      ? formatBalance(data.value.toString(10))
      : '0'
  }, [data, status])

  const btnCln = clsx('w-full flex justify-around mt-[20px]', {
    'btn1-light': !isDark,
    'btn1-dark': isDark,
  })

  const checkPublicKeyAndEnodeId = async (_: any, value: string) => {
    if (value === undefined) {
      return Promise.reject(new Error('Invalid public key'))
    }
    if (String(value).startsWith('0x') && value.length === 130) {
      return Promise.resolve()
    } else {
      return Promise.reject(new Error('Invalid public key'))
    }
  }

  const checkAmount = async (_: any, value: string) => {
    if (value === undefined || !checkAmountUnit(value)) {
      return Promise.reject(new Error('Invalid amount'))
    }
    if (
      new BigNumber(value).lt(
        formatEther(BigInt(OpenStoremanGroupRegister.minStakeIn)),
      )
    ) {
      return Promise.reject(new Error('Minimum amount should be 10000 WAN'))
    }
    if (new BigNumber(value).gte(balance)) {
      return Promise.reject(
        new Error('Please make sure you have sufficient balance'),
      )
    }
    return Promise.resolve()
  }

  const handleNext = () => {
    form
      .validateFields()
      .then(() => {
        if (new BigNumber(balance).minus(form.getFieldValue('amount')).lte(0)) {
          message.error('The cost exceeds the balance. Please try again')
          return
        }
        setTransParam({
          crosschain: `${OpenStoremanGroupRegister.chain1[2]} <-> ${OpenStoremanGroupRegister.chain2[2]}`,
          groupId: OpenStoremanGroupRegister.groupId,
          PK: form.getFieldValue('publicKey'),
          enodeID: form.getFieldValue('enodeID'),
          amount: form.getFieldValue('amount'),
        })
        setStep(12)
      })
      .catch((err: any) => console.log(err))
  }

  const groupId = useMemo(() => {
    return OpenStoremanGroupRegister && OpenStoremanGroupRegister.groupId
      ? hexCharCodeToStr(OpenStoremanGroupRegister.groupId)
      : 'N/A'
  }, [OpenStoremanGroupRegister])

  return (
    <div
      className={clsx('flex-1', {
        'stwoTwoSty-dark': isDark,
        'stwoTwoSty-light': !isDark,
      })}
    >
      <div className="border-b-1 border-b-light-555 dark:border-b-dark-666">
        <p className="text-3xl text-light-111 mb-[15px] dark:text-dark-111">
          Register Node
        </p>
      </div>
      <div className="flex mt-[15px] dark:text-white ">
        <div className="w-[10px] mr-4">1.</div>
        <pre className="font-[16px] flex flex-col gap-2 overflow-auto whitespace-pre-wrap break-words">
          <div className="flex items-center gap-2">
            <span className="font-semibold text-light-0 font-inter dark:text-white">
              Generate Public Key and EnodeID
            </span>
            <img
              src={CopyCode}
              alt="Copy code"
              className="w-5 h-5 object-contain flex-shrink-0 cursor-pointer hover:opacity-80 transition-opacity"
              onClick={() => handleCopyCode('generate')}
            />
          </div>
          <span className="dark:text-dark-0">
            Run the following script in your Ubuntu environment to obtain your
            Public Key and EnodeID:
          </span>
          <pre id="generate" className="mb-0">
            <code className="text-light-111 whitespace-pre-wrap break-words">
              wget
              https://raw.githubusercontent.com/wanchain/two-way-bridge-contracts/master/helpscript/envSetup.sh
              && chmod +x envSetup.sh && ./envSetup.sh
            </code>
          </pre>
        </pre>
      </div>
      <div className="mt-[10px] rounded-2xl py-4 px-6 bg-light-888 dark:bg-dark-333 dark:text-dark-0">
        <div className="font-semibold">Note:</div>
        <div>
          Back up both the script output and the osm folder. If running locally,
          you’ll later need to copy the osm folder to your cloud server.
        </div>
      </div>
      <div className="flex mt-[15px] dark:text-white">
        <div className="w-[10px] mr-4">2.</div>
        <div className="font-[16px] flex flex-col gap-2">
          <span className="font-semibold text-light-0 font-inter dark:text-white">
            Register as a Bridge Node Candidate
          </span>
          <span className="dark:text-dark-0">
            Enter your <span className="font-semibold">Public Key,</span>{' '}
            <span className="font-semibold">EnodeID</span>, and{' '}
            <span className="font-semibold">Staked Amount</span>.
          </span>
        </div>
      </div>
      <div className="border-1 border-light-555 rounded-2xl mt-[10px] p-[10px] dark:border-dark-666">
        <span className="dark:text-white">My Bridge Node Account</span>
        <InputContainer
          className={clsx(
            'mb-[20px] mt-[10px] flex flex-col gap-[19px] w-full',
            {
              'inputDisabled-dark': isDark,
              'inputDisabled-light': !isDark,
            },
          )}
        >
          <Input
            style={{ textAlign: 'left' }}
            className="disabledSty"
            disabled
            addonBefore="Group ID"
            value={groupId}
            autoComplete="off"
          />
        </InputContainer>
        <InputContainer
          className={clsx('mt-[10px] flex flex-col', {
            'inputDisabled-dark': isDark,
            'inputDisabled-light': !isDark,
          })}
        >
          <Form form={form}>
            <Form.Item
              name="publicKey"
              rules={[
                {
                  required: true,
                  validator: checkPublicKeyAndEnodeId,
                },
              ]}
            >
              <Input
                autoComplete="off"
                defaultValue={tarnsParam.PK || ''}
                className="canRegistSty"
                disabled={!canRegist}
                addonBefore="Public Key"
                placeholder="Enter public key"
              />
            </Form.Item>
          </Form>
          <Form form={form}>
            <Form.Item
              name="enodeID"
              rules={[{ required: true, validator: checkPublicKeyAndEnodeId }]}
            >
              <Input
                autoComplete="off"
                defaultValue={tarnsParam.enodeID || ''}
                className="canRegistSty"
                disabled={!canRegist}
                addonBefore="Enode ID"
                placeholder="Up to two decimal places"
              />
            </Form.Item>
          </Form>
        </InputContainer>
      </div>
      <div className="border-1 border-light-555 rounded-2xl mt-[10px] p-[10px] dark:border-dark-666">
        <div className="dark:text-white">My Wallet Account</div>
        <InputContainer
          className={clsx('mt-[10px] flex flex-col gap-[19px]', {
            'inputDisabled-dark': isDark,
            'inputDisabled-light': !isDark,
          })}
        >
          <Input
            autoComplete="off"
            className="disabledSty"
            disabled
            addonBefore="Address"
            value={address}
          />
          <Input
            autoComplete="off"
            className="disabledSty"
            addonBefore="Balance"
            disabled
            value={balance}
          />

          <Form form={form}>
            <Form.Item
              name="amount"
              rules={[{ required: true, validator: checkAmount }]}
            >
              <Input
                autoComplete="off"
                className="canRegistSty"
                disabled={!canRegist}
                addonBefore="Amount"
                placeholder="0"
                defaultValue={tarnsParam.amount || '0'}
              />
            </Form.Item>
          </Form>
        </InputContainer>
      </div>
      <div className={btnCln}>
        <Button
          disabled={!canRegist}
          onClick={() => setStep(0)}
          className="w-[47%] rounded-2xl h-[60px] text-[18px]"
        >
          Previous
        </Button>
        <Button
          disabled={!canRegist}
          className="w-[47%] rounded-2xl h-[60px] btn text-[18px]"
          onClick={handleNext}
        >
          Next
        </Button>
      </div>
    </div>
  )
}

const InputContainer = styled.div`
  .ant-input-group-addon {
    color: #0f68aa;
    border-top-left-radius: 10px;
    border-bottom-left-radius: 10px;
  }

  .ant-input {
    height: 35px;
    border-top-right-radius: 10px;
    border-bottom-right-radius: 10px;
  }

  .ant-input:hover {
    border-color: none;
  }
`
